import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  path?: string;
  image?: string;
  jsonLd?: Record<string, unknown>;
}

const SITE_NAME = 'Kottayil Temple Muchukunnu — Sri Kotta-Kovilakam Kshethram';
const BASE_URL = 'https://kottakovilakam.com';
const DEFAULT_IMAGE = `${BASE_URL}/assets/og-image.jpg`;

export default function SEO({
  title,
  description = 'Kottayil Temple (Kottayil Shiva Temple) at Muchukunnu, Kozhikode, Kerala — an ancient Hindu temple famous for its sacred Kottayil Kulam (temple pond). Book poojas online, donate, explore festivals and rich temple history.',
  keywords,
  path = '',
  image = DEFAULT_IMAGE,
  jsonLd,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const url = `${BASE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Kottayil Temple Muchukunnu" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Language alternates */}
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="ml" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Page-level JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
